import { useState } from "react";
import { toast } from "sonner";
import { Star, ChevronUp, ChevronDown, X, Trash2, FileDown, Loader2, CheckCircle2, AlertTriangle, Globe } from "lucide-react";
import DATA from "@/data/vaseis2026.json";
import { FIELDS, fmt } from "@/data/vaseisFields";
import { exportVaseisPdf } from "@/lib/vaseisPdf";

const BY_CODE = Object.fromEntries(DATA.map((s) => [s.c, s]));

function Status({ base, moria }) {
  if (moria == null) return null;
  if (base <= moria) return <span data-testid="mylist-status-yes" className="inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Πιάνεις</span>;
  if (base - moria <= 300) return <span data-testid="mylist-status-close" className="inline-flex items-center gap-1 font-mono-accent text-[10px] uppercase tracking-widest text-amber-700"><AlertTriangle className="h-3 w-3" /> Οριακά</span>;
  return <span data-testid="mylist-status-no" className="font-mono-accent text-[10px] uppercase tracking-widest text-red-700">−{fmt(base - moria)}</span>;
}

export default function MyList({ fav, moriaFor, onClose }) {
  const [exporting, setExporting] = useState(false);
  const rows = fav.codes.map((c) => BY_CODE[c]).filter(Boolean);
  const my = (s) => (moriaFor ? moriaFor(s) : null);
  const reached = moriaFor ? rows.filter((s) => my(s) != null && my(s) >= s.b).length : null;

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportVaseisPdf({ field: { id: "lista", short: "Η λίστα μου", name: "Προσωπική λίστα σχολών (σειρά προτίμησης)" }, rows, moriaFor });
      toast.success("Το PDF της λίστας δημιουργήθηκε.");
    } catch { toast.error("Αποτυχία δημιουργίας PDF."); } finally { setExporting(false); }
  };

  return (
    <aside data-testid="mylist-panel" className="bg-white border-2 border-primary rounded-sm p-5 md:p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500 fill-amber-400" /> Η λίστα μου
            <span className="font-mono-accent text-xs text-muted-foreground font-normal" data-testid="mylist-count">({rows.length} σχολές)</span>
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Η σειρά είναι η σειρά προτίμησης στο μηχανογραφικό — άλλαξέ τη με τα βελάκια.
            {reached != null && <> Με τους βαθμούς σου «πιάνεις» <strong data-testid="mylist-reached">{reached}</strong> από {rows.length}.</>}
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="mylist-export" onClick={handleExport} disabled={exporting || rows.length === 0} className="inline-flex items-center gap-2 h-10 px-4 bg-primary text-primary-foreground font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:bg-accent transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
          </button>
          <button data-testid="mylist-clear" onClick={() => { if (window.confirm("Να αδειάσει η λίστα;")) fav.clear(); }} disabled={rows.length === 0} className="inline-flex items-center gap-2 h-10 px-3 border border-border font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:border-red-600 hover:text-red-700 transition-colors disabled:opacity-50">
            <Trash2 className="h-4 w-4" />
          </button>
          <button data-testid="mylist-close" onClick={onClose} aria-label="Κλείσιμο" className="inline-flex items-center justify-center h-10 w-10 border border-border rounded-sm hover:border-primary transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p data-testid="mylist-empty" className="py-8 text-center text-sm text-muted-foreground">Δεν έχεις ακόμη σχολές. Πάτησε το <Star className="inline h-4 w-4 -mt-0.5" /> δίπλα σε μια σχολή για να την προσθέσεις.</p>
      ) : (
        <ol className="divide-y divide-border">
          {rows.map((s, i) => {
            const field = FIELDS.find((f) => s.f.includes(f.id));
            return (
              <li key={s.c} data-testid={`mylist-item-${s.c}`} className="py-3 flex items-center gap-3">
                <span className="font-heading font-extrabold text-xl text-primary w-8 text-right shrink-0">{i + 1}</span>
                <div className="flex flex-col shrink-0">
                  <button data-testid={`mylist-up-${s.c}`} onClick={() => fav.move(s.c, -1)} disabled={i === 0} className="h-5 w-6 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-20"><ChevronUp className="h-4 w-4" /></button>
                  <button data-testid={`mylist-down-${s.c}`} onClick={() => fav.move(s.c, 1)} disabled={i === rows.length - 1} className="h-5 w-6 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-20"><ChevronDown className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm leading-snug">{s.n}</span>
                    <Status base={s.b} moria={my(s)} />
                  </div>
                  <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                    <span>{s.i} · Κωδ. {s.c}</span>
                    <span style={{ color: field?.color }}>{field?.short}</span>
                    {s.j?.[0] && <span className="normal-case tracking-normal">→ {s.j[0]}</span>}
                    {s.u && <a href={s.u} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><Globe className="h-3 w-3" /> Ιστοσελίδα</a>}
                  </div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="font-heading font-extrabold text-lg">{fmt(s.b)}</div>
                  {my(s) != null && <div className="font-mono-accent text-[10px] text-muted-foreground">εσύ: {fmt(my(s))}</div>}
                </div>
                <button data-testid={`mylist-remove-${s.c}`} onClick={() => fav.toggle(s.c)} aria-label="Αφαίρεση" className="h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-red-700 transition-colors shrink-0"><X className="h-4 w-4" /></button>
              </li>
            );
          })}
        </ol>
      )}
    </aside>
  );
}
